import { userRoles, userTypes } from 'utils/constants';
import { isStaffEmail } from 'utils/helpers/email.helper';

/**
 * The API answers a team member it refuses with one of these codes and a `userMessage` written for the screen.
 *   MAX_MANAGERS_REACHED         - the org is at its `seats.manager` (invite, convert, or a type change to manager)
 *   MAX_GUARDS_REACHED           - the same for `seats.guard`, a limit of 0 included (the org has no guard seats)
 *   MAX_VIRTUAL_GUARDS_REACHED   - the same for `seats.virtual_guard`
 *   MAX_MANAGERS_BELOW_CURRENT   - a new `seats.manager` below the managers and pending invites the org already has
 *   MAX_GUARDS_BELOW_CURRENT / MAX_VIRTUAL_GUARDS_BELOW_CURRENT - the same for `seats.guard` / `seats.virtual_guard`
 *   PRIMARY_MANAGER_TYPE_LOCKED  - the org's only primary manager cannot be given another type
 *   PRIMARY_MANAGER_REMOVE_LOCKED - the org's only primary manager cannot be removed
 */
export const MANAGER_LIMIT_ERROR_CODES = [
    'MAX_MANAGERS_REACHED',
    'MAX_GUARDS_REACHED',
    'MAX_VIRTUAL_GUARDS_REACHED',
    'MAX_MANAGERS_BELOW_CURRENT',
    'MAX_GUARDS_BELOW_CURRENT',
    'MAX_VIRTUAL_GUARDS_BELOW_CURRENT',
    'PRIMARY_MANAGER_TYPE_LOCKED',
    'PRIMARY_MANAGER_REMOVE_LOCKED'
];

/**
 * The seat limits of an org (`org.seats`). A kind's `key` is the key in `seats` and also the `type` of the team members
 * (role `operator`) it counts, as in the API. `minimum` is the lowest limit the API accepts: an org always has a manager,
 * and a limit of 0 means none for guards and virtual guards.
 */
export const SEAT_KINDS = [
    { key: 'manager', label: 'Managers', minimum: 1 },
    { key: 'guard', label: 'Guards', minimum: 0 },
    { key: 'virtual_guard', label: 'Virtual guards', minimum: 0 }
];

/** The limit of one kind (`org.seats[kind]`); null when the org has none. */
export const seatLimitOf = (org, kind) => {
    const limit = org?.seats?.[kind];
    return typeof limit === 'number' ? limit : null;
};

/**
 * The members the API counts against the limit of one kind: role `operator` and that `type`, Sage staff (`isStaffEmail`)
 * left out. The `active` flag does not matter. Pending invites count too, but the org document does not carry them.
 */
export const countSeats = (users, kind) =>
    (users || []).filter(
        user =>
            user?.role === userRoles.OPERATOR &&
            user?.type === kind &&
            !isStaffEmail(user?.email)
    ).length;

/** The manager count the API's limit uses (`countSeats` for managers). */
export const countManagers = users => countSeats(users, userTypes.MANAGER);

/** The manager limit of an org (`org.seats.manager`); null when the org has no limit. */
export const maxManagersOf = org => seatLimitOf(org, userTypes.MANAGER);

/**
 * "Seats" of an org: its managers out of the most it may have, e.g. `3 / 5`. Only the managers in the org count, not the
 * invited ones. An org without a limit shows `no limit`.
 */
export const formatSeats = (users, maxManagers) =>
    `${countManagers(users)} / ${
        typeof maxManagers === 'number' ? maxManagers : 'no limit'
    }`;

/** The hover text of a Seats value (`formatSeats`): what the left number counts, and what it leaves out. */
export const SEATS_TOOLTIP =
    'Managers in the organization, excluding Sage staff. Pending invites also count toward the limit but are not shown here.';

/** The message to show when `error` is one of the seat-limit refusals above, else null. */
export const managerLimitErrorMessage = error =>
    MANAGER_LIMIT_ERROR_CODES.includes(error?.code)
        ? error.userMessage || error.message || null
        : null;

/**
 * The API refuses a Sage staff address added to a team by anyone but a super admin with this code. `message` says why
 * (for the console and the Network tab); `userMessage` is the neutral text for the screen and the only one to show.
 */
export const USER_CANNOT_BE_ADDED_CODE = 'USER_CANNOT_BE_ADDED';

/** The message to show when `error` is the API refusing to add that user, else null. */
export const userCannotBeAddedMessage = error =>
    error?.code === USER_CANNOT_BE_ADDED_CODE ? error.userMessage || null : null;

/** The snackbar text for a manager that could not be removed: the API's own message when it refused, else the error. */
export const removeManagerErrorMessage = error =>
    managerLimitErrorMessage(error) ||
    `Error removing manager: ${error?.message || 'Unexpected error'}`;

/** The API client rejects with the response body; `message` is an array for validation errors. */
export const apiErrorMessage = error => {
    const message = error?.userMessage || error?.message || error;
    return Array.isArray(message)
        ? message.join(', ')
        : String(message || 'Unknown error');
};

/**
 * The invites of one kind still waiting for an answer, out of the org's pending invites (the same list the Users tab
 * loads): role `operator`, that `type`, not an external (global virtual guard) invite, not a Sage staff address
 * (`isStaffEmail`). They count against the limit.
 */
export const countInvitedSeats = (invites, kind) =>
    (invites || []).filter(
        invite =>
            invite?.role === userRoles.OPERATOR &&
            invite?.type === kind &&
            invite?.external !== true &&
            !isStaffEmail(invite?.email)
    ).length;

/** The manager invites still waiting for an answer (`countInvitedSeats` for managers). */
export const countInvitedManagers = invites =>
    countInvitedSeats(invites, userTypes.MANAGER);

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const SEAT_NOUNS = { manager: 'manager', guard: 'guard', virtual_guard: 'virtual guard' };

/**
 * What is wrong with a typed limit of one kind, or null: a whole number (1 or more for managers, 0 or more for guards
 * and virtual guards, where 0 means none) that is not below what the org already has of that kind, its members plus
 * its invited ones (`counts`: `{ members, invites }`; the API counts the same and has the last say).
 */
export const seatLimitError = (kind, value, { members = 0, invites = 0 } = {}) => {
    const isManager = kind === userTypes.MANAGER;
    const text = String(value ?? '').trim();
    if (!(isManager ? /^[1-9]\d*$/ : /^(0|[1-9]\d*)$/).test(text)) {
        return `Enter a whole number of ${isManager ? 1 : 0} or more.`;
    }
    const inUse = members + invites;
    if (Number(text) < inUse) {
        const noun = SEAT_NOUNS[kind];
        return `The limit cannot be lower than ${inUse}: ${plural(
            members,
            noun
        )} and ${plural(invites, `invited ${noun}`)} already in this organization.`;
    }
    return null;
};

/** `seatLimitError` for the manager limit, with the counts named as the manager form always had them. */
export const maxManagersError = (value, { managers = 0, invites = 0 } = {}) =>
    seatLimitError(userTypes.MANAGER, value, { members: managers, invites });

/**
 * What one Save sends: the kinds whose typed value differs from the stored limit, as numbers. A blank field is left
 * out (a limit cannot be cleared, so a blank on a stored limit is reported by `seatLimitError`, not sent). `typed` and
 * `saved` are keyed by kind; the caller sends this only once every field passes `seatLimitError`.
 */
export const changedSeats = (typed, saved) =>
    SEAT_KINDS.reduce((changes, { key }) => {
        const text = String(typed?.[key] ?? '').trim();
        if (text !== '' && Number(text) !== saved?.[key]) {
            changes[key] = Number(text);
        }
        return changes;
    }, {});

/** The kind each API refusal of a limit set too low belongs to, so its message goes under that field. */
export const BELOW_CURRENT_KINDS = {
    MAX_MANAGERS_BELOW_CURRENT: 'manager',
    MAX_GUARDS_BELOW_CURRENT: 'guard',
    MAX_VIRTUAL_GUARDS_BELOW_CURRENT: 'virtual_guard'
};
